import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AbsencesPerYearListDataSource } from './absences-per-year-list-datasource';
import { BehaviorSubject, combineLatest, firstValueFrom, startWith } from 'rxjs';
import { Router } from '@angular/router';
import moment from 'moment';
import { SubSink } from 'SubSink';
import { TranslateService } from '@ngx-translate/core';
import { Filter } from 'src/app/models/common/filter';
import { DatePipe } from '@angular/common';
import { animate, style, transition, trigger } from '@angular/animations';
import { NgxSpinnerService } from 'ngx-spinner';
import { ScreenService } from 'src/app/core/screen.service';
import { Moment } from 'moment';
import { MatDatepicker } from '@angular/material/datepicker';
import { RomanMonths } from 'src/app/models/common/roman-months';
import { FilterService } from 'src/app/list-filter/services/filter.service';
import { AbsencesPerYearListFilterService } from '../services/AbsencesPerYearListFilterService';

@Component({
  selector: 'app-absences-per-year-list',
  templateUrl: './absences-per-year-list.component.html',
  styleUrl: './absences-per-year-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe, { provide: FilterService, useClass: AbsencesPerYearListFilterService }],
  animations: [
    trigger('inOutAnimation', [
      transition(':enter', [style({ opacity: 0 }), animate('1s ease-out', style({ opacity: 1 }))]),
      transition(':leave', [style({ opacity: 1 }), animate('1s ease-in', style({ opacity: 0 }))]),
    ]),
  ],
  standalone: false
})
export class AbsencesPerYearListComponent implements AfterViewInit, OnInit, OnDestroy {
  dataSource = inject(AbsencesPerYearListDataSource);
  screenService = inject(ScreenService);
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private datePipe = inject(DatePipe);
  private spinner = inject(NgxSpinnerService);
  private filterService = inject(FilterService);

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  set currentDate(value: Date) {
    this.currentDate$.next(value);
  }
  get currentDate() {
    return this.currentDate$.value;
  }

  private currentDate$ = new BehaviorSubject<Date>(new Date());
  private readonly subs = new SubSink();

  filters: Filter[] = [];
  displayedColumns = [];
  romanMonths: string[] = RomanMonths.MONTHS;

  private _areFiltersExpanded: boolean;
  public get areFiltersExpanded(): boolean {
    return this._areFiltersExpanded;
  }
  public set areFiltersExpanded(value: boolean) {
    if (this._areFiltersExpanded !== value) {
      this._areFiltersExpanded = value;
    }
  }

  constructor() {
    this.initTabColumns();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  async ngOnInit(): Promise<void> {
    this.currentDate = moment(this.router.url.replace('/absences/', ''), 'YYYY').toDate();
    this.initLoader();

    this.filterService.filters$.subscribe((filters) => {
      this.filterData(filters);
    });
  }

  ngAfterViewInit(): void {
    this.subs.sink = combineLatest([
      this.sort.sortChange.pipe(startWith(null)),
      this.paginator.page.pipe(startWith(null)),
      this.translateService.onLangChange.pipe(startWith(null)),
    ]).subscribe(() => this.fetchAbsences());
  }

  initTabColumns() {
    this.displayedColumns = [
      ...['workerFullName', 'company', 'employer', 'employerObject', 'employmentDateFrom', 'employmentDateTo'],
      ...this.romanMonths,
      ...['absenceSum'],
    ];
  }

  previousYear() {
    this.currentDate = new Date(this.currentDate.addYears(-1));
    this.setNavigation();
    this.fetchAbsences();
  }

  nextYear() {
    this.currentDate = new Date(this.currentDate.addYears(1));
    this.setNavigation();
    this.fetchAbsences();
  }

  onDateChange(event: Moment, picker?: MatDatepicker<Moment>): void {
    picker.close();
    this.currentDate = event.toDate();
    this.setNavigation();
    this.fetchAbsences();
  }

  toggleFiltersPanel() {
    this.areFiltersExpanded = !this.areFiltersExpanded;
  }

  async filterData(filters: Filter[]): Promise<void> {
    this.filters = filters;
    this.fetchAbsences();
  }

  async resetFilters(): Promise<void> {
    await firstValueFrom(this.filterService.resetFilters());
  }

  private setNavigation() {
    this.router.navigateByUrl(`absences/${this.datePipe.transform(this.currentDate, 'yyyy')}`);
  }

  private initLoader() {
    this.subs.sink = this.dataSource.isLoading$.subscribe((isLoading) => (isLoading ? this.spinner.show() : this.spinner.hide()));
  }

  private fetchAbsences(): void {
    this.dataSource.absencesSubject.next({
      Year: this.currentDate.getFullYear(),
      Page: this.paginator.pageIndex + 1,
      PageSize: this.paginator.pageSize,
      SortingField: this.sort.active,
      SortingDirection: this.sort.direction,
      Filters: this.filters,
    });
  }
}
